import { useEffect, useRef, useState } from "react";
import { Auth, setAuth } from "@/lib/api";
import { Link, navigate } from "@/lib/router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validatePassword, PASSWORD_HINT } from "@/lib/password";
import { LoginCaptcha, type LoginCaptchaHandle } from "@/components/LoginCaptcha";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef<LoginCaptchaHandle>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setErr(""); setLoading(true);
    try {
      const cap = await captchaRef.current?.collect();
      if (cap === null) { setErr("请先完成人机验证"); setLoading(false); return; }
      const t = await Auth.login({ email, password, ...(cap || {}) });
      setAuth(t.access_token, t.refresh_token);
      let back = "/me";
      try { back = sessionStorage.getItem("post_login") || back; sessionStorage.removeItem("post_login"); } catch {}
      navigate(back);
    } catch (e: any) {
      setErr(e?.body?.message || "登录失败");
      captchaRef.current?.reset();
    }
    finally { setLoading(false); }
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>登录</CardTitle>
          <CardDescription>使用邮箱密码登录账户</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">邮箱</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pwd">密码</Label>
              <Input id="pwd" type="password" required value={password} onChange={(e) => setPwd(e.target.value)} />
            </div>
            <LoginCaptcha ref={captchaRef} />
            {err && <p className="text-sm text-destructive">{err}</p>}
            <Button type="submit" className="w-full" disabled={loading}>{loading ? "登录中..." : "登录"}</Button>
            <p className="text-sm text-center text-muted-foreground">
              还没账号？<Link to="/register" className="text-primary hover:underline">立即注册</Link>
              <span className="mx-2">·</span>
              <Link to="/forgot" className="text-primary hover:underline">忘记密码？</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function RegisterPage() {
  const [accountType, setAccountType] = useState<"individual" | "enterprise">("individual");
  const [companyName, setCompanyName] = useState("");
  const [creditCode, setCreditCode] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPwd] = useState("");
  const [name, setName] = useState("");
  const [cfg, setCfg] = useState<{
    verify_email: boolean; verify_sms: boolean;
    allow_email: boolean; allow_phone: boolean;
  } | null>(null);
  const [emailCode, setEmailCode] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [smsCooldown, setSmsCooldown] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef<LoginCaptchaHandle>(null);

  // 加载注册配置（only the page-level fields here；captcha 由 LoginCaptcha 内部独立拉取）
  useEffect(() => {
    Auth.registerConfig().then((c) => setCfg({
      verify_email: c.verify_email, verify_sms: c.verify_sms,
      allow_email: c.allow_email, allow_phone: c.allow_phone,
    })).catch(() => setCfg({ verify_email: false, verify_sms: false, allow_email: true, allow_phone: true }));
  }, []);

  // 倒计时
  useEffect(() => {
    if (emailCooldown <= 0) return;
    const t = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCooldown]);
  useEffect(() => {
    if (smsCooldown <= 0) return;
    const t = setTimeout(() => setSmsCooldown(smsCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [smsCooldown]);

  const sendEmail = async () => {
    if (!email) { setErr("请先输入邮箱"); return; }
    setErr(""); setEmailSending(true);
    try { await Auth.sendEmailCode(email); setEmailCooldown(60); }
    catch (e: any) { setErr(e?.body?.message || "邮箱验证码发送失败"); }
    finally { setEmailSending(false); }
  };
  const sendSms = async () => {
    if (!phone) { setErr("请先输入手机号"); return; }
    setErr(""); setSmsSending(true);
    try { await Auth.sendSmsCode(phone); setSmsCooldown(60); }
    catch (e: any) { setErr(e?.body?.message || "短信验证码发送失败"); }
    finally { setSmsSending(false); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !cfg) return;
    setErr("");
    if (cfg.verify_email && !emailCode) { setErr("请填写邮箱验证码"); return; }
    if (cfg.verify_sms && !smsCode) { setErr("请填写短信验证码"); return; }
    const ruleErr = validatePassword(password);
    if (ruleErr) { setErr(ruleErr); return; }

    const captchaPayload = await captchaRef.current?.collect();
    if (captchaPayload === null) { setErr("请先完成人机验证"); return; }

    setLoading(true);
    try {
      await Auth.register({
        email, phone: phone || undefined, password, display_name: name,
        ...(captchaPayload || {}),
        email_code: cfg.verify_email ? emailCode : undefined,
        sms_code: cfg.verify_sms ? smsCode : undefined,
        account_type: accountType,
        company_name: accountType === "enterprise" ? companyName : undefined,
        unified_credit_code: accountType === "enterprise" ? creditCode : undefined,
        contact_name: accountType === "enterprise" ? contactName : undefined,
      });
      const t = await Auth.login({ email, password });
      setAuth(t.access_token, t.refresh_token);
      navigate("/me");
    } catch (e: any) {
      setErr(e?.body?.message || "注册失败");
      captchaRef.current?.reset();
    }
    finally { setLoading(false); }
  };

  if (!cfg) return <div className="container mx-auto px-4 py-16 max-w-md text-center text-muted-foreground">加载中…</div>;

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>创建账户</CardTitle>
          <CardDescription>免费注册，畅览公开招标信息</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>账号类型</Label>
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setAccountType("individual")}
                  className={`flex-1 py-2 rounded-md border text-sm ${accountType === "individual" ? "border-primary bg-primary/10 text-primary" : "border-input"}`}>
                  个人
                </button>
                <button type="button"
                  onClick={() => setAccountType("enterprise")}
                  className={`flex-1 py-2 rounded-md border text-sm ${accountType === "enterprise" ? "border-primary bg-primary/10 text-primary" : "border-input"}`}>
                  企业
                </button>
              </div>
            </div>
            {accountType === "enterprise" && (
              <>
                <div className="space-y-1.5">
                  <Label>公司名称 *</Label>
                  <Input required value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>统一社会信用代码</Label>
                  <Input value={creditCode} onChange={(e) => setCreditCode(e.target.value)} placeholder="选填" />
                </div>
                <div className="space-y-1.5">
                  <Label>联系人</Label>
                  <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>邮箱</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {cfg.verify_email && (
              <div className="space-y-1.5">
                <Label>邮箱验证码</Label>
                <div className="flex gap-2">
                  <Input required value={emailCode} onChange={(e) => setEmailCode(e.target.value)} placeholder="请查收邮件" className="min-w-0" />
                  <Button type="button" variant="outline" onClick={sendEmail}
                    disabled={emailSending || emailCooldown > 0 || !email} className="shrink-0">
                    {emailCooldown > 0 ? `${emailCooldown}s` : (emailSending ? "发送中" : "发送验证码")}
                  </Button>
                </div>
              </div>
            )}
            {cfg.verify_sms && (
              <>
                <div className="space-y-1.5">
                  <Label>手机号</Label>
                  <Input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>短信验证码</Label>
                  <div className="flex gap-2">
                    <Input required value={smsCode} onChange={(e) => setSmsCode(e.target.value)} placeholder="请查收短信" className="min-w-0" />
                    <Button type="button" variant="outline" onClick={sendSms}
                      disabled={smsSending || smsCooldown > 0 || !phone} className="shrink-0">
                      {smsCooldown > 0 ? `${smsCooldown}s` : (smsSending ? "发送中" : "发送验证码")}
                    </Button>
                  </div>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>{accountType === "enterprise" ? "显示名称" : "昵称"}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>密码</Label>
              <Input type="password" required minLength={8} value={password} onChange={(e) => setPwd(e.target.value)} />
              <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
            </div>
            <LoginCaptcha ref={captchaRef} scope="register" />
            {err && <p className="text-sm text-destructive">{err}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "注册中..." : "注册"}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              已有账号？<Link to="/login" className="text-primary hover:underline">登录</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [step, setStep] = useState<1 | 2>(1);
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [target, setTarget] = useState("");
  const [cap, setCap] = useState<{ id: string; image: string } | null>(null);
  const [capCode, setCapCode] = useState("");
  const [capErr, setCapErr] = useState("");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const refreshCap = () => {
    Auth.captcha().then(setCap).catch((e: any) => setCapErr(e?.body?.message || "验证码加载失败"));
  };
  useEffect(() => { refreshCap(); }, []);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setErr(""); setMsg(""); setLoading(true);
    try {
      await Auth.resetRequest({ channel, target, captcha_id: cap!.id, captcha_code: capCode });
      setMsg("如该账号存在，验证码已发送，请查收。");
      setStep(2);
    } catch (e: any) { setErr(e?.body?.message || "发送失败"); refreshCap(); setCapCode(""); }
    finally { setLoading(false); }
  };

  const reset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setErr("");
    const ruleErr = validatePassword(pwd);
    if (ruleErr) { setErr(ruleErr); return; }
    setLoading(true);
    try {
      await Auth.resetConfirm({ channel, target, code, new_password: pwd });
      setMsg("密码已重置，请使用新密码登录。");
      setTimeout(() => navigate("/login"), 1200);
    } catch (e: any) { setErr(e?.body?.message || "重置失败"); }
    finally { setLoading(false); }
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>找回密码</CardTitle>
          <CardDescription>{step === 1 ? "选择渠道接收验证码" : "输入验证码并设置新密码"}</CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={send} className="space-y-4">
              <div className="space-y-1.5">
                <Label>渠道</Label>
                <select className="h-9 w-full rounded-md border bg-background px-3" value={channel}
                  onChange={(e) => setChannel(e.target.value as any)}>
                  <option value="email">邮箱</option>
                  <option value="sms">手机</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>{channel === "email" ? "邮箱" : "手机号"}</Label>
                <Input required type={channel === "email" ? "email" : "tel"} value={target} onChange={(e) => setTarget(e.target.value)} />
              </div>
              {cap ? (
                <div className="space-y-1.5">
                  <Label>图形验证码</Label>
                  <div className="flex gap-2">
                    <Input required value={capCode} onChange={(e) => setCapCode(e.target.value)} placeholder="请输入图中字符" />
                    <button type="button" onClick={refreshCap} className="shrink-0">
                      <img src={cap.image} alt="captcha" className="h-10 rounded-md border" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-destructive">{capErr || "验证码加载中..."}</div>
              )}
              {err && <p className="text-sm text-destructive">{err}</p>}
              {msg && <p className="text-sm text-emerald-600">{msg}</p>}
              <Button type="submit" className="w-full" disabled={loading || !cap}>{loading ? "发送中..." : "发送验证码"}</Button>
              <p className="text-sm text-center text-muted-foreground">
                <Link to="/login" className="text-primary hover:underline">返回登录</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={reset} className="space-y-4">
              <div className="text-sm text-muted-foreground">向 <b>{target}</b> 发送了 6 位验证码</div>
              <div className="space-y-1.5">
                <Label>验证码</Label>
                <Input required value={code} onChange={(e) => setCode(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>新密码</Label>
                <Input required type="password" minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} />
                <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
              </div>
              {err && <p className="text-sm text-destructive">{err}</p>}
              {msg && <p className="text-sm text-emerald-600">{msg}</p>}
              <Button type="submit" className="w-full" disabled={loading}>{loading ? "重置中..." : "重置密码"}</Button>
              <p className="text-sm text-center text-muted-foreground">
                <button type="button" className="text-primary hover:underline" onClick={() => setStep(1)}>重新发送</button>
                <span className="mx-2">·</span>
                <Link to="/login" className="text-primary hover:underline">返回登录</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
